export interface OrdatChannel {
    id: string;
    name: string;
}

export interface OrdatIntegration {
    id: string;
    createdAt: string;
    updatedAt: string;
    name: string;
    baseUrl: string;
    username: string;
    proxy: string;
    companyName: string;
    businessLine: string;
    businessSubLine: string;
    cultureName: string;
    // the GraphQL type resolves the Django choices field to an enum: WEBSHOP / PREORDER
    orderPushMode: string;
    defaultShipmentType: string;
    headerTextEnabled: boolean;
    headerTextContextId: string;
    itemTextEnabled: boolean;
    itemTextContextId: string;
    itemAdditionalTextContextId: number | null;
    fallbackPartNumber: string;
    sellablePartsOnly: boolean;
    attachSourceDocuments: boolean;
    priceCheckLowerThresholdPercentage: number | null;
    priceCheckUpperThresholdPercentage: number | null;
    // capabilities and integrationParameters are graphene JSONString scalars: JSON encoded as a string
    capabilities: string | null;
    integrationParameters: string | null;
    useOutputMapping: boolean;
    excludedRecordTypes: string[];
    hasCaBundle: boolean;
    orderChannels: OrdatChannel[];
    rfqChannels: OrdatChannel[];
    masterDataUpdateActive: boolean;
    masterDataUpdatedAt: string | null;
    masterDataUpdateError: string | null;
    fetchProgress: number;
}

export interface OrdatParameterDefinition {
    key: string;
    type: string;
    // also a JSONString: the default already arrives JSON encoded
    default: string | null;
    description: string;
}

export interface OrdatConnectionTestResult {
    ok: boolean;
    warnings: string[];
    errors: string[];
    capabilities: string | null;
    fossRelease: number | null;
    fossCompanyName: string | null;
    businessLine: string | null;
    businessSubLine: string | null;
}

export interface OrdatFormData {
    name: string;
    baseUrl: string;
    username: string;
    password: string;
    proxy: string;
    caBundle: string;
    companyName: string;
    businessLine: string;
    businessSubLine: string;
    cultureName: string;
    orderPushMode: string;
    defaultShipmentType: string;
    headerTextEnabled: boolean;
    headerTextContextId: string;
    itemTextEnabled: boolean;
    itemTextContextId: string;
    itemAdditionalTextContextId: string;
    fallbackPartNumber: string;
    sellablePartsOnly: boolean;
    attachSourceDocuments: boolean;
    priceCheckLowerThresholdPercentage: string;
    priceCheckUpperThresholdPercentage: string;
    integrationParameters: Record<string, any>;
    useOutputMapping: boolean;
    orderChannelIds: string[];
    rfqChannelIds: string[];
}

export const ORDER_PUSH_MODE_WEBSHOP = 'webshop';
export const ORDER_PUSH_MODE_PREORDER = 'preorder';

export const FORM_DEFAULT_VALUES: OrdatFormData = {
    name: '',
    baseUrl: '',
    username: '',
    password: '',
    proxy: '',
    caBundle: '',
    companyName: '',
    businessLine: '',
    businessSubLine: '',
    cultureName: 'de-DE',
    orderPushMode: ORDER_PUSH_MODE_WEBSHOP,
    defaultShipmentType: '',
    headerTextEnabled: true,
    headerTextContextId: '',
    itemTextEnabled: true,
    itemTextContextId: '',
    itemAdditionalTextContextId: '',
    fallbackPartNumber: '',
    sellablePartsOnly: false,
    attachSourceDocuments: false,
    priceCheckLowerThresholdPercentage: '0',
    priceCheckUpperThresholdPercentage: '0',
    integrationParameters: {},
    useOutputMapping: false,
    orderChannelIds: [],
    rfqChannelIds: [],
};

/** Parse a decimal field value that may use a comma as the decimal separator. */
export const parseDecimalField = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
};

/** Parse an integer field value; empty input means "not set" and is sent as null. */
export const parseIntegerField = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isInteger(parsed) ? parsed : null;
};

/** Decode a graphene JSONString into an object; anything unparsable becomes an empty object. */
export const parseJsonStringObject = (value: string | null | undefined): Record<string, any> => {
    if (!value) return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

/** The order push mode is stored lowercase but read back as an uppercase GraphQL enum member. */
export const normalizeOrderPushMode = (value: string | null | undefined): string =>
    (value || ORDER_PUSH_MODE_WEBSHOP).toLowerCase();

/** Capability maps carry a `checked_at` timestamp next to the boolean operation flags. */
export const CAPABILITY_CHECKED_AT_KEY = 'checked_at';
